import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

const STORAGE_KEY = "vf_impersonated_tenant";

type ImpersonationValue = {
  activeTenantId: string | null;
  setActiveTenantId: (id: string | null) => void;
  clear: () => void;
};

const ImpersonationContext = createContext<ImpersonationValue | null>(null);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });

  useEffect(() => {
    if (activeTenantId) {
      window.localStorage.setItem(STORAGE_KEY, activeTenantId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [activeTenantId]);

  const setActiveTenantId = useCallback((id: string | null) => {
    setActiveTenantIdState(id);
  }, []);

  const clear = useCallback(() => setActiveTenantIdState(null), []);

  return (
    <ImpersonationContext.Provider value={{ activeTenantId, setActiveTenantId, clear }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation must be used inside ImpersonationProvider");
  return ctx;
}
